import React, { useState, useEffect } from "react";
import { Dropdown } from "react-bootstrap";
import { BsGrid3X3GapFill, BsFillEnvelopeFill } from "react-icons/bs";
import BellIconWithNotifications, {
  NotificationWidget,
} from "../notifications/notificationwidget";
import { IoMap } from "react-icons/io5";
import { FiRefreshCw } from "react-icons/fi";
import "bootstrap/dist/css/bootstrap.min.css";
import BackIcon from "../../../assets/images/back-icon.svg";
import { useNavigate, useLocation } from "react-router-dom";

const EditRole = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const apiUrl = process.env.REACT_APP_API_URL;

  // Get roleId from navigation state or URL params
  const roleId = location.state?.roleId;

  // State management
  const [roleName, setRoleName] = useState("");
  const [originalRoleName, setOriginalRoleName] = useState("");
  const [permissions, setPermissions] = useState({});
  const [originalPermissions, setOriginalPermissions] = useState({});
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [roleLoading, setRoleLoading] = useState(false);
  const [error, setError] = useState("");
  const [userName, setUserName] = useState("");

  // Fetch available permissions from API
  const fetchPermissions = async () => {
    try {
      setPermissionsLoading(true);
      const token = localStorage.getItem("accessToken");

      if (!token) {
        setError("Authentication token not found");
        return;
      }

      const response = await fetch(`${apiUrl}renewals/permissions`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAvailablePermissions(data.data || []);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Failed to fetch permissions");
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setError("Failed to fetch permissions");
    } finally {
      setPermissionsLoading(false);
    }
  };

  // Fetch specific role data
  const fetchRoleData = async () => {
    if (!roleId) {
      setError("Role ID not provided");
      return;
    }

    try {
      setRoleLoading(true);
      const token = localStorage.getItem("accessToken");

      if (!token) {
        setError("Authentication token not found");
        return;
      }

      // Fetch all roles and find the specific one
      const response = await fetch(`${apiUrl}renewals/roles`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        const roles = data.data || [];
        const currentRole = roles.find((role) => role.id === roleId);

        if (currentRole) {
          setRoleName(currentRole.role);
          setOriginalRoleName(currentRole.role);

          // Parse permissions JSON and set state
          let rolePermissions = [];
          try {
            rolePermissions = JSON.parse(currentRole.permissions || "[]");
          } catch (e) {
            console.error("Error parsing permissions:", e);
            rolePermissions = [];
          }

          // Set permissions state based on role's permissions
          const permissionsState = {};
          // First, set all permissions to false
          availablePermissions.forEach((perm) => {
            permissionsState[perm.id] = false;
          });
          // Then, set role's permissions to true
          rolePermissions.forEach((permId) => {
            permissionsState[permId] = true;
          });

          setPermissions(permissionsState);
          setOriginalPermissions({ ...permissionsState });
        } else {
          setError("Role not found");
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Failed to fetch role data");
      }
    } catch (error) {
      console.error("Error fetching role data:", error);
      setError("Failed to fetch role data");
    } finally {
      setRoleLoading(false);
    }
  };

  // Update role
  const updateRole = async () => {
    if (!roleName.trim()) {
      setError("Please enter a role name.");
      return;
    }

    // Get selected permissions
    const selectedPermissions = Object.keys(permissions)
      .filter((permId) => permissions[permId])
      .map((permId) => parseInt(permId));

    if (selectedPermissions.length === 0) {
      setError("Please select at least one permission.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("accessToken");

      const updateData = {
        lastUpdatedBy: userName || "system",
      };

      // Only include fields that have changed
      if (roleName.trim() !== originalRoleName) {
        updateData.role = roleName.trim();
      }

      // Check if permissions have changed
      const originalSelectedPermissions = Object.keys(originalPermissions)
        .filter((permId) => originalPermissions[permId])
        .map((permId) => parseInt(permId));

      const permissionsChanged =
        selectedPermissions.length !== originalSelectedPermissions.length ||
        !selectedPermissions.every((permId) =>
          originalSelectedPermissions.includes(permId)
        );

      if (permissionsChanged) {
        updateData.permissions = JSON.stringify(selectedPermissions);
      }

      // Check if anything has changed
      if (Object.keys(updateData).length === 1) {
        // Only lastUpdatedBy
        setError("No changes detected.");
        return;
      }

      const response = await fetch(`${apiUrl}renewals/roles/${roleId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        alert(`Role "${roleName}" updated successfully!`);
        navigate("/renewals-manage-roles");
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Failed to update role");
      }
    } catch (error) {
      console.error("Error updating role:", error);
      setError("Failed to update role");
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    // Get user info from localStorage
    const user = JSON.parse(localStorage.getItem("user"));
    if (user && user.name) {
      setUserName(user.name);
    }

    // Check if roleId is provided
    if (!roleId) {
      setError("Role ID not provided. Redirecting...");
      setTimeout(() => navigate("/renewals-manage-roles"), 2000);
      return;
    }

    // Fetch permissions first
    fetchPermissions();
  }, [roleId]);

  // Fetch role data after permissions are loaded
  useEffect(() => {
    if (availablePermissions.length > 0 && roleId) {
      fetchRoleData();
    }
  }, [availablePermissions, roleId]);

  const handleRoleNameChange = (e) => {
    setRoleName(e.target.value);
    setError(""); // Clear error when user starts typing
  };

  const handleBack = () => {
    navigate("/renewals-manage-roles");
  };

  const handlePermissionChange = (permissionId) => {
    setPermissions((prev) => ({
      ...prev,
      [permissionId]: !prev[permissionId],
    }));
    setError(""); // Clear error when user selects permissions
  };

  const handleUpdate = () => {
    updateRole();
  };

  const handleCancel = () => {
    // Reset to original values
    setRoleName(originalRoleName);
    setPermissions({ ...originalPermissions });
    setError("");
    navigate("/renewals-manage-roles");
  };

  const handleSelectAll = () => {
    const allSelected = availablePermissions.every(
      (perm) => permissions[perm.id]
    );
    const newPermissions = {};
    availablePermissions.forEach((perm) => {
      newPermissions[perm.id] = !allSelected;
    });
    setPermissions(newPermissions);
  };

  // Check if there are any changes
  const hasChanges = () => {
    if (roleName.trim() !== originalRoleName) return true;

    const currentSelectedPermissions = Object.keys(permissions)
      .filter((permId) => permissions[permId])
      .sort();
    const originalSelectedPermissions = Object.keys(originalPermissions)
      .filter((permId) => originalPermissions[permId])
      .sort();

    return (
      JSON.stringify(currentSelectedPermissions) !==
      JSON.stringify(originalSelectedPermissions)
    );
  };

  if (!roleId) {
    return (
      <div className="content-body">
        <div className="alert alert-danger">
          Role ID not provided. Redirecting to manage roles...
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="dashboard-title mb-3">
        <div className="d-flex gap-2">
          <button
            className="btn p-0 me-3 text-dark"
            onClick={handleBack}
            style={{ fontSize: "1.5rem" }}
          >
            <img src={BackIcon} alt="Back" />
          </button>
          <h1>Edit Role</h1>
        </div>
        <div className="d-flex align-items-center gap-3">
          <BellIconWithNotifications />
          <div className="dropdown-container">
            <Dropdown>
              <Dropdown.Toggle
                variant="light"
                id="dropdown-basic"
                className="rounded-circle border-0 bg-transparent"
              >
                <div
                  className="menu-icon"
                  style={{
                    fontSize: "1.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BsGrid3X3GapFill />
                </div>
              </Dropdown.Toggle>

              <Dropdown.Menu className="dropdown-menu-custom">
                <Dropdown.Item
                  href="/dashboard"
                  className="dropdown-item-custom"
                >
                  <BsFillEnvelopeFill className="dropdown-icon" /> Quote/Bind
                  Email
                </Dropdown.Item>
                <Dropdown.Item
                  href="/geographical-concentration"
                  className="dropdown-item-custom"
                >
                  <IoMap className="dropdown-icon" /> Geographical Concentration
                </Dropdown.Item>
                <Dropdown.Item
                  href="/renewals-dashboard"
                  className="dropdown-item-custom"
                >
                  <FiRefreshCw className="dropdown-icon" /> Renewals
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>

      <div className="content-body mb-3">
        {/* Error Display */}
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
            <button
              type="button"
              className="btn-close float-end"
              onClick={() => setError("")}
              aria-label="Close"
            ></button>
          </div>
        )}

        {/* Loading State */}
        {(permissionsLoading || roleLoading) && (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">
              {permissionsLoading
                ? "Loading permissions..."
                : "Loading role data..."}
            </p>
          </div>
        )}

        {/* Form Content */}
        {!permissionsLoading && !roleLoading && (
          <>
            {/* Role Name Input */}
            <div className="mb-4">
              <label htmlFor="roleName" className="form-label fw-medium">
                Role Name *
              </label>
              <input
                type="text"
                className="form-control"
                id="roleName"
                placeholder="Enter role name"
                value={roleName}
                onChange={handleRoleNameChange}
                style={{ maxWidth: "400px" }}
                disabled={loading}
              />
            </div>

            {/* Permissions Section */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <label className="form-label fw-medium mb-0">
                  Permissions *
                </label>
                {availablePermissions.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={handleSelectAll}
                    disabled={loading}
                  >
                    {availablePermissions.every((perm) => permissions[perm.id])
                      ? "Deselect All"
                      : "Select All"}
                  </button>
                )}
              </div>

              {/* Permissions Grid */}
              {availablePermissions.length > 0 && (
                <div className="row g-3 mb-3">
                  {availablePermissions.map((permission) => (
                    <div key={permission.id} className="col-md-4">
                      <div className="check-grid">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`permission-${permission.id}`}
                            checked={permissions[permission.id] || false}
                            onChange={() =>
                              handlePermissionChange(permission.id)
                            }
                            disabled={loading}
                          />
                          <label
                            className="form-check-label fw-medium"
                            htmlFor={`permission-${permission.id}`}
                          >
                            {permission.permission}
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Selected Permissions Count */}
              {availablePermissions.length > 0 && (
                <div className="d-flex justify-content-between">
                  <small className="text-muted">
                    Selected:{" "}
                    {Object.values(permissions).filter(Boolean).length} of{" "}
                    {availablePermissions.length} permissions
                  </small>
                  {hasChanges() && (
                    <small className="text-warning">
                      <i className="fas fa-exclamation-triangle me-1"></i>
                      Unsaved changes
                    </small>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="d-flex gap-3">
              <button
                className="btn text-white px-4"
                onClick={handleUpdate}
                disabled={loading || !hasChanges()}
                style={{ backgroundColor: "#122066", border: "none" }}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Updating...
                  </>
                ) : (
                  "Update"
                )}
              </button>
              <button
                className="btn btn-outline-secondary px-4"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default EditRole;
